import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector, connect } from "react-redux";
import { reduxForm, Field, initialize, formValueSelector } from "redux-form";
import {
  Form,
  Button,
  Icon,
  Grid,
  List,
  Menu,
  Container,
  Header,
} from "semantic-ui-react";
import {
  TextInput,
  SelectInput,
  AdvertiserOptions,
} from "../utils/form-fields";
import { createBanner, updateBanner } from "../../firebase";
import MultiDrop from "../utils/multi-drop";
import { imageKeys } from "./presets";
import { CancelButton } from "../utils/buttons";
import { checkUser } from "../utils/helpers";

let BannerForm = ({
  change,
  submitting,
  pristine,
  handleSubmit,
  invalid,
  retailer,
  imagesGroup,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const banner = useSelector((state) => state.banner);
  const advertisers = useSelector((state) => state.advertisers || []);
  const user = useSelector((state) => state.user);
  const retailers = useSelector((state) => state.retailers);

  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    dispatch(initialize("bannerForm", banner));
    setShowLoading(false);
  }, [dispatch, banner]);

  useEffect(() => {
    const { pathname } = location;
    const arr = pathname.split("/");
    if (
      !arr.find((path) => {
        return path === "new";
      })
    ) {
      if (!checkUser(user, banner.advertiser, retailer)) navigate("/");
    }
  }, [location, user, banner.advertiser, retailer, navigate]);

  const handleImageRemove = (key) => {
    change(key, {});
  };

  const getAdvertiser = (advertiser) => {
    return _.find(advertisers, (item) => {
      return item.displayName === advertiser;
    });
  };

  const getTemplateId = async (type) => {
    const templates = await _.filter(retailers, (item) => {
      return item.name === retailer;
    });
    return _.filter(templates[0].templates, (template) => {
      return template.name === type;
    });
  };

  const cleanTag = (tag) => {
    if (tag.indexOf("?icid") !== -1) {
      let splitUrl = tag.split("?icid");
      tag = splitUrl[0];
    }
    return tag.lastIndexOf("/") === tag.length - 1
      ? tag.slice(0, tag.length - 1)
      : tag;
  };

  const handleSubmitForm = async (values) => {
    setShowLoading(true);
    let advertiserId = await getAdvertiser(values.advertiser);
    advertiserId = advertiserId.advertiserId;
    let templateId = values.templateId;
    if (!templateId) {
      templateId = await getTemplateId(values.bannerType);
      templateId = templateId[0].id;
    }
    let bespokeTemplate = true;
    let templateType = "bespoke";
    let appLink = cleanTag(values.bannerURL);
    const submitValues = {
      ...values,
      retailer,
      advertiserId,
      templateId,
      bespokeTemplate,
      templateType,
      appLink,
    };
    submitValues.lastModBy = `${user.displayName} (${user.organisation})`;
    banner.key
      ? updateBanner(submitValues, banner.key).then((message) => {
          //console.log("updated banner");
        })
      : createBanner(submitValues).then((message) => {
          if (message.id) navigate(`/${retailer}/banner/${message.id}`);
          //console.log("created new banner");
        });
  };

  return (
    <Form onSubmit={handleSubmit(handleSubmitForm)}>
      <Grid padded className="brand-form">
        <Grid.Row>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Banner info</Header>
            <Field
              name="bannerTitle"
              label="Title"
              type="text"
              component={TextInput}
              placeholder="Add a banner title"
            />
            <Field
              name="adNumber"
              label="Ad number"
              type="text"
              component={TextInput}
              placeholder="Add an ad number"
            />
            <div className="field">
              <div className="field">
                <label htmlFor="advertiser">Advertiser</label>
                <Field
                  name="advertiser"
                  component="select"
                  multiple={false}
                  className="ui selection dropdown"
                >
                  {!user.restrictByAdvertiser && (
                    <option value="">Choose an advertiser</option>
                  )}
                  <AdvertiserOptions advertisers={advertisers} />
                </Field>
              </div>
            </div>
            <Field
              name="bannerURL"
              label="Banner link"
              type="text"
              component={TextInput}
              placeholder="Add a URL link"
            />
            <Field
              name="tescoEvent"
              label="Tesco event"
              type="text"
              component={TextInput}
              placeholder="Add an Event name"
            />
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Banner text</Header>
            <Field
              name="alt"
              label="ALT text"
              type="text"
              component={TextInput}
              placeholder="Add ALT text"
            />
            <Field
              name="includeRegularPrice"
              label="Regular price"
              type="text"
              component={SelectInput}
              options={[
                { text: "yes", value: "yes", key: "yes" },
                { text: "no", value: "no", key: "no" },
              ]}
              multiple={false}
            />
            <Field
              name="hideMPU"
              label="Hide MPU"
              type="text"
              component={SelectInput}
              options={[
                { text: "yes", value: "yes", key: "yes" },
                { text: "no", value: "no", key: "no" },
              ]}
              multiple={false}
            />
            <Field
              name="openLinkOptions"
              label="Open link option"
              type="text"
              component={SelectInput}
              options={[
                { text: "_top", value: "_top", key: "_top" },
                { text: "_blank", value: "_blank", key: "_blank" },
              ]}
              multiple={false}
            />
            <Field
              name="informationBanner"
              label="Information banner"
              type="text"
              component={SelectInput}
              options={[
                { text: "yes", value: "yes", key: "yes" },
                { text: "no", value: "no", key: "no" },
              ]}
              multiple={false}
            />
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Images</Header>
            <List size="medium">
              {Object.keys(imagesGroup).map((key) => {
                return (
                  <List.Item key={key}>
                    {imagesGroup[key].path && (
                      <List.Content>
                        <List.Header>{key}</List.Header>
                        <div className="img-holder">
                          <img
                            src={imagesGroup[key].path}
                            alt={imagesGroup[key].name}
                          />
                          <span>
                            {imagesGroup[key].name}{" "}
                            <span>Image size: {imagesGroup[key].size}</span>
                          </span>
                          <Button
                            icon
                            onClick={() => handleImageRemove(key)}
                            type="button"
                            size="mini"
                            color="red"
                          >
                            <Icon name="trash" />
                          </Button>
                        </div>
                      </List.Content>
                    )}
                  </List.Item>
                );
              })}
            </List>
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Upload</Header>
            <div className="header">Drop zone</div>
            <MultiDrop
              folder={banner.key || "brand-template"}
              fileKeys={imageKeys}
              change={change}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Menu fixed="bottom" size="mini">
        <Container>
          <Menu.Item>
            <Button
              disabled={submitting || pristine || invalid || showLoading}
              positive
              type="submit"
              loading={showLoading}
              size="mini"
            >
              Save
            </Button>
          </Menu.Item>
          <Menu.Item>
            <CancelButton
              id={banner.key}
              location={location}
              retailer={retailer}
              pristine={pristine}
              size="mini"
            />
          </Menu.Item>
        </Container>
      </Menu>
    </Form>
  );
};

const validate = (values) => {
  const isUrlValid = (url) => {
    return url.match(
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
    );
  };

  const hasWhiteSpace = (hex) => {
    return hex.match(/\s/g);
  };

  const errors = {};

  if (!values.bannerTitle) {
    errors.bannerTitle = "Please enter a title";
  }
  if (!values.adNumber || isNaN(Number(values.adNumber))) {
    errors.adNumber = "Please enter a number only";
  }
  if (values.bannerURL) {
    if (!isUrlValid(values.bannerURL)) {
      errors.bannerURL = "Please enter a valid URL";
    }
    if (isUrlValid(values.bannerURL)) {
      if (hasWhiteSpace(values.bannerURL)) {
        errors.bannerURL = "Please remove any spaces";
      }
    }
  }
  if (!values.alt) {
    errors.alt = "Please add an alternative (alt) description";
  }

  return errors;
};

BannerForm = reduxForm({
  validate,
  form: "bannerForm",
})(BannerForm);

const selector = formValueSelector("bannerForm");
BannerForm = connect((state) => {
  const advertiserValue = selector(state, "advertiser");
  const imagesGroup = selector(
    state,
    "mobile375x110",
    "mobile375x258",
    "tablet532x110",
    "tablet724x110",
    "desktop918x110",
    "widedesktop984x120",
    "widedesktop1486x120",
    "widedesktop1486x180",
    "buylistHeader"
  );
  return {
    advertiserValue,
    imagesGroup,
  };
})(BannerForm);

export default BannerForm;
